<script module lang="ts">
	export interface RFInspectorControlsProps {
		onexpand?: (e: MouseEvent) => void;
		onzoomout?: (e: MouseEvent) => void;
		onzoomin?: (e: MouseEvent) => void;
		onfullscreen?: (e: MouseEvent) => void;
		onclose?: (e: MouseEvent) => void;
		ondeleteall?: (e: MouseEvent) => void;
		oncolumnschange?: (columns: number) => void;
		ongridmodechange?: (mode: 'horizontal' | 'vertical' | 'masonry') => void;
		onmodechange?: (mode: 'canvas' | 'grid' | 'light-table') => void;
		isExpanded?: boolean;
		isFullscreen?: boolean;
		isMobile?: boolean;
		hasCanvases?: boolean;
		gridMode?: 'horizontal' | 'vertical' | 'masonry';
		gridColumns?: number;
		mode?: 'canvas' | 'grid' | 'light-table';
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import RFInspectorControl from './rf-inspector-control.svelte';
	import classNames from 'classnames';
	import cssVariables from '@src/utils/cssvariables';
	import Columns from '@core/elements/icons/columns.svelte';
	/**
	 * Props
	 */
	let {
		onexpand = () => {},
		onzoomout = () => {},
		onzoomin = () => {},
		onfullscreen = () => {},
		ondeleteall = () => {},
		oncolumnschange = () => {},
		ongridmodechange = () => {},
		onmodechange = () => {},
		onclose = () => {},
		isExpanded = false,
		gridMode = 'horizontal',
		gridColumns = 3,
		mode = 'canvas',
		isFullscreen = false,
		isMobile = false,
		hasCanvases = false,
	}: RFInspectorControlsProps = $props();

	const GRID_MAX_COLUMNS = 5;
	const modesLabels = {
		canvas: 'Canvas',
		grid: 'Grid',
		'light-table': 'Light Table',
	} as const;

	type Mode = keyof typeof modesLabels;

	let showModes: boolean = $state(false);

	const onActiveModeClick = () => {
		showModes = !showModes;
	};

	const onModeChange = (newMode: Mode) => {
		mode = newMode;
		showModes = false;
		onmodechange?.(newMode);
	};

	const onGridChange = (type: 'add' | 'subtract') => {
		const newColumns = type === 'add' ? gridColumns + 1 : gridColumns - 1;
		if (newColumns >= 1 && newColumns <= GRID_MAX_COLUMNS) {
			gridColumns = newColumns;
			oncolumnschange(gridColumns);
		}
	};

	const onCanvasFunction = (e: MouseEvent, type: 'expand' | 'zoom-out' | 'zoom-in') => {
		showModes = false;
		switch (type) {
			case 'expand':
				onexpand(e);
				break;
			case 'zoom-out':
				onzoomout(e);
				break;
			case 'zoom-in':
				onzoomin(e);
				break;
		}
	};

	const onGridFunction = (type: 'masonry' | 'vertical' | 'horizontal') => {
		showModes = false;
		ongridmodechange?.(type);
	};
</script>

<div class="rf-inspector-controls">
	{#if !isMobile}
		<RFInspectorControl icon={mode} ariaLabel="Modes" onclick={onActiveModeClick} active />
		<div
			class={classNames('rf-inspector-controls__modes', { 'rf-inspector-controls__modes--active': showModes })}
			style={cssVariables({
				'--total-modes': Object.keys(modesLabels).length - 1,
			})}
		>
			{#each Object.keys(modesLabels) as md, i}
				{#if md !== mode}
					<RFInspectorControl
						icon={md as Mode}
						ariaLabel={modesLabels[md as Mode]}
						onclick={() => onModeChange(md as Mode)}
						style={cssVariables({
							'--mode-index': i,
						})}
					/>
				{/if}
			{/each}
		</div>
	{/if}
	{#if hasCanvases}
		{#if mode === 'canvas'}
			<div class="rf-inspector-controls__canvas">
				{#if !isMobile}
					<RFInspectorControl
						icon={isExpanded ? 'minimize' : 'expand'}
						ariaLabel={isExpanded ? 'Minimize' : 'Expand'}
						onclick={(e) => onCanvasFunction(e, 'expand')}
					/>
				{/if}
				<RFInspectorControl icon="zoom-out" ariaLabel="Zoom Out" onclick={(e) => onCanvasFunction(e, 'zoom-out')} />
				<RFInspectorControl icon="zoom-in" ariaLabel="Zoom In" onclick={(e) => onCanvasFunction(e, 'zoom-in')} />
			</div>
		{/if}
		{#if mode === 'grid'}
			<div class="rf-inspector-controls__grid">
				<RFInspectorControl
					icon="horizontal"
					ariaLabel="Horizontal layout"
					onclick={() => onGridFunction('horizontal')}
					active={gridMode === 'horizontal'}
				/>
				<RFInspectorControl
					icon="vertical"
					ariaLabel="Vertical layout"
					onclick={() => onGridFunction('vertical')}
					active={gridMode === 'vertical'}
				/>
				<RFInspectorControl
					icon="masonry"
					ariaLabel="Masonry layout"
					onclick={() => onGridFunction('masonry')}
					active={gridMode === 'masonry'}
				/>
				<div class="rf-inspector-controls__grid__columns" title="Columns" aria-label="Columns">
					<Columns />
					<p class="rf-inspector-controls__grid__columns--count">{gridColumns}</p>
					<div class="rf-inspector-controls__grid__columns__btns">
						<button
							title="Add Columns"
							aria-label="Add Columns"
							class="rf-inspector-controls__grid__columns__btns--btn"
							onclick={() => onGridChange('add')}
							disabled={gridColumns >= GRID_MAX_COLUMNS}
						>
							+
						</button>
						<button
							title="Subtract Columns"
							aria-label="Subtract Columns"
							class="rf-inspector-controls__grid__columns__btns--btn"
							onclick={() => onGridChange('subtract')}
							disabled={gridColumns <= 1}
						>
							-
						</button>
					</div>
				</div>
			</div>
		{/if}
	{/if}
	{#if !isMobile}
		<RFInspectorControl
			icon={isFullscreen ? 'exit-fullscreen' : 'fullscreen'}
			ariaLabel={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
			onclick={onfullscreen}
		/>
	{/if}
	{#if hasCanvases}
		<RFInspectorControl icon="trash" ariaLabel="Remove all canvases" onclick={ondeleteall} />
	{/if}
	<RFInspectorControl icon="close" ariaLabel="Close inspector" onclick={onclose} />
</div>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-inspector-controls {
		position: absolute;
		bottom: 8px;
		width: auto;
		height: auto;
		left: 50%;
		transform: translateX(-50%);
		z-index: 15;
		gap: var(--rf-spacing-xs, 4px);
		background-color: var(--rf-inspector-control-bg-color);
		backdrop-filter: blur(4px);
		border: solid var(--rf-inspector-control-border-color) 1px;
		border-radius: 8px;
		padding: var(--rf-spacing-xs, 4px);

		display: flex;
		justify-content: center;
		align-items: center;
		&__modes {
			overflow: hidden;
			height: fit-content;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;
			gap: var(--rf-spacing-xs, 4px);
			position: absolute;
			left: var(--rf-spacing-xs, 4px);
			top: calc((-100% * var(--total-modes)) + (var(--total-modes) * var(--rf-spacing-xs, 4px)));
			pointer-events: none;
			--rf-inspector-control-bg-color: rgba(35, 35, 35);
			--rf-inspector-control-hover-color: rgb(70, 70, 70);
			.rf-inspector-control {
				opacity: 0;
				transform: translateY(100%);
				transition:
					transform 0.3s ease-in-out,
					opacity 0.3s ease;
				transition-delay: calc(var(--mode-index) * 0.05s);
			}
			&--active {
				pointer-events: all;
				.rf-inspector-control {
					opacity: 1;
					transform: translateY(0);
				}
			}
		}
		&__canvas,
		&__grid {
			display: flex;
			position: relative;
			align-items: center;
			gap: var(--rf-spacing-xs, 4px);
			padding: 0 calc(var(--rf-spacing-xs, 4px) + 1px);
			&::before,
			&::after {
				content: '';
				display: block;
				position: absolute;
				width: 1px;
				height: 80%;
				background-color: rgba(255, 255, 255, 0.2);
			}
			&::before {
				left: 0;
			}
			&::after {
				right: 0;
			}

			@media screen and (max-width: breakpoint.$s) {
				padding-left: 0;
				&::before {
					display: none;
				}
			}
		}
		&__grid {
			&__columns {
				display: flex;
				align-items: center;
				overflow: hidden;
				gap: 8px;
				background-color: var(--rf-inspector-control-bg-color);
				padding-left: var(--rf-spacing-xs, 4px);
				border: solid var(--rf-inspector-control-border-color) 1px;
				border-radius: var(--rf-inspector-control-border-radius, 8px);
				color: var(--rf-inspector-control-label-color, #fff);
				svg {
					flex-grow: 0;
					flex-shrink: 0;
					width: 16px;
					height: 16px;
				}
				&__btns {
					display: flex;
					align-items: center;
					flex-direction: column;
					height: var(--rf-inspector-control-height, 40px);
					border-left: solid var(--rf-inspector-control-border-color) 1px;
					position: relative;
					&::before {
						content: '';
						display: block;
						position: absolute;
						width: 80%;
						height: 1px;
						background-color: var(--rf-inspector-control-border-color, rgba(255, 255, 255, 0.2));
						top: 50%;
						left: 50%;
						transform: translate(-50%, -50%);
					}
					&--btn {
						width: 20px;
						height: 20px;
						border: none;
						display: flex;
						transition: 0.3s ease background;
						background-color: var(--rf-inspector-control-bg-color);
						color: var(--rf-inspector-control-label-color, #fff);
						cursor: pointer;
						align-items: center;
						justify-content: center;
						&:hover {
							background-color: var(--rf-inspector-control-hover-color, rgba(255, 255, 255, 0.2));
						}
						&:disabled {
							opacity: 0.5;
							cursor: not-allowed;
							background-color: rgba(255, 255, 255, 0.1);
						}
					}
				}
				p {
					font-size: 12px;
					text-transform: uppercase;
					font-family: var(--rf-headings-font, 'Arial', sans-serif);
					color: var(--rf-inspector-control-label-color, #fff);
				}
			}
		}
	}
</style>
